import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom';
import { css } from '@emotion/css';
import { color_colors_ocean, color_text_light, color_shades_light, ocean_blue, color_shades_darkest, color_shades_darker, color_defaults_white } from '../../constants/colors';
import { ResponsiveLine } from '@nivo/line';
import SecondaryLoader from '../general/SecondaryLoader';
import { tooltipStyles } from '../../constants/chartTheme';
import moment from 'moment';

const primaryIndicator = require('../../content/images/analytics-icons/histogram-primary-indicator.svg');
const secondaryIndicator = require('../../content/images/analytics-icons/histogram-secondary-indicator.svg');

export type HistogramViewOption = "Containable Conversations" | "Order Calls" | "Spanish Calls" | "All Conversations" | "Average Calls by Hour" | "Average Calls by Day of Week" | "Concurrent Calls";

// Multi-line histogram chart component
interface HistogramChartMultiLineProps {
    data: Array<{ id: string, data: { x: string, y: number, color: string, fullDate: string }[], color: string }>
    paddingBottom?: number
    padding?: number
    hasSubtitle?: boolean
    scale?: number
}

const HistogramChartMultiLine: React.FC<HistogramChartMultiLineProps> = (props) => {
    // Detect if this is hourly data
    const isHourlyData = props.data.length > 0 && props.data[0]?.data?.length > 1 && (() => {
        const firstDate = moment(props.data[0].data[0].fullDate).format('YYYY-MM-DD');
        const secondDate = moment(props.data[0].data[1].fullDate).format('YYYY-MM-DD');
        return firstDate === secondDate;
    })();

    // Track if we're on mobile for margin adjustments
    const [isMobile, setIsMobile] = React.useState(false);
    // Laptop breakpoint detection — same pattern as ChartTooltip.tsx and TransfersChart.tsx
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const isChromebook = screenWidth >= 1024 && screenWidth < 1280;
    const isSmallLaptop = screenWidth >= 1280 && screenWidth < 1440;
    const ttBody = isChromebook ? '10px' : isSmallLaptop ? '11px' : '12px';
    const ttValue = isChromebook ? '13px' : isSmallLaptop ? '14px' : '16px';

    React.useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 640);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const chartContainerStyle = css`
        height: 100%;
        width: 100%;
        overflow: hidden;
        padding: 0;
        font-family: Muli;
        flex: 1;
        min-height: 0;

        tspan {
            font-family: Muli;
            font-size: 9px;
            font-weight: normal;
            
            @media (max-width: 640px) {
                font-size: 8px;
            }
        }
    `;

    const tooltipStyle = css`
        position: fixed;
        background: ${tooltipStyles.container.background};
        backdrop-filter: ${tooltipStyles.container.backdropFilter};
        padding: ${tooltipStyles.container.padding};
        border-radius: ${tooltipStyles.container.borderRadius};
        box-shadow: ${tooltipStyles.container.boxShadow};
        border: ${tooltipStyles.container.border};
        min-width: ${tooltipStyles.container.minWidth};
        z-index: 99999;
        pointer-events: none;
        .date-label {
            font-weight: ${tooltipStyles.title.fontWeight};
            font-size: ${tooltipStyles.title.fontSize};
            line-height: 20px;
            color: ${tooltipStyles.title.color};
            margin-bottom: ${tooltipStyles.title.marginBottom}px;
            @media (min-width: 1280px) and (max-width: 1439.98px) {
                font-size: 12px;
            }
            @media (min-width: 1024px) and (max-width: 1279.98px) {
                font-size: 11px;
            }
        }
        .value-label {
            font-size: 13px;
            color: ${color_shades_darker};
            @media (min-width: 1280px) and (max-width: 1439.98px) {
                font-size: 11px;
            }
            @media (min-width: 1024px) and (max-width: 1279.98px) {
                font-size: 10px;
            }
        }
    `;

    // Portal-based tooltip component that follows cursor
    const PortalTooltip: React.FC<{ slice: any }> = ({ slice }) => {
        const [position, setPosition] = React.useState({ x: 0, y: 0 });

        React.useEffect(() => {
            // Skip mouse tracking for mobile compact mode
            if (isMobile) return;

            const handleMouseMove = (e: MouseEvent) => {
                setPosition({ x: e.clientX, y: e.clientY });
            };

            document.addEventListener('mousemove', handleMouseMove);
            return () => document.removeEventListener('mousemove', handleMouseMove);
        }, [isMobile]);

        // Format the date for display (day of week and date only, no time, unless hourly data)
        const formatDate = (dateString: string): string => {
            if (!dateString) return '';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                ...(isHourlyData ? { hour: 'numeric', minute: '2-digit' } : {})
            });
        };

        // Format date for compact mobile display (shorter)
        const formatDateCompact = (dateString: string): string => {
            if (!dateString) return '';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            });
        };

        // Collect values and colors from actual data points in the slice
        const seriesMap: Record<string, { value: number; color: string }> = {};

        // Helper to clean series name (remove trailing .0, .1, etc. that nivo may append)
        const cleanSeriesName = (name: string): string => {
            return name?.replace(/\.\d+$/, '') || 'Unknown';
        };

        slice.points.forEach((p: any) => {
            // nivo ResponsiveLine with enableSlices="x" provides serieId on slice points
            // Also check serie.id as a fallback for different nivo versions
            const rawSeriesName = p.serieId ?? p.serie?.id ?? p.id ?? 'Unknown';
            const seriesName = cleanSeriesName(String(rawSeriesName));
            const yValue = p.data?.y ?? p.y ?? 0;
            const seriesColor = p.serieColor ?? p.serie?.color ?? p.color ?? color_shades_darker;

            if (!seriesMap[seriesName]) {
                seriesMap[seriesName] = { value: yValue, color: seriesColor };
            } else {
                seriesMap[seriesName].value += yValue;
            }
        });

        // Determine if this is the "All Conversations" view with nested series (contained ⊆ containable ⊆ all)
        const allCallsValue = seriesMap['All Conversations']?.value;
        const containableCallsValue = seriesMap['Containable Conversations']?.value;
        const containedCallsValue = seriesMap['Contained Conversations']?.value;
        const isAllCallsView = allCallsValue !== undefined && containableCallsValue !== undefined && containedCallsValue !== undefined;

        // Check for partial "All Conversations" view (when only 2 of 3 series present, like Containable Conversations view)
        const isContainableCallsView = !isAllCallsView && containableCallsValue !== undefined && containedCallsValue !== undefined;

        // For Spanish calls view
        const allSpanishCallsValue = seriesMap['All Spanish Calls']?.value;
        const spanishContainableValue = seriesMap['Spanish Containable Conversations']?.value;
        const spanishContainedValue = seriesMap['Spanish Contained Conversations']?.value;
        const isSpanishCallsView = allSpanishCallsValue !== undefined && spanishContainableValue !== undefined && spanishContainedValue !== undefined;

        // Calculate percentages for nested series
        const getPercentage = (value: number, parent: number): string => {
            if (parent === 0) return '0%';
            return `${Math.round((value / parent) * 100)}%`;
        };

        // Build display data with percentages for nested views
        const getDisplayData = (): Array<{ name: string; value: number; color: string; percentage?: string }> => {
            if (isAllCallsView) {
                // All Conversations view: show raw values + % of parent
                // Contained is % of Containable, Containable is % of All Conversations
                return [
                    {
                        name: 'All Conversations',
                        value: allCallsValue,
                        color: seriesMap['All Conversations'].color
                    },
                    {
                        name: 'Containable Conversations',
                        value: containableCallsValue,
                        color: seriesMap['Containable Conversations'].color,
                        percentage: getPercentage(containableCallsValue, allCallsValue)
                    },
                    {
                        name: 'Contained Conversations',
                        value: containedCallsValue,
                        color: seriesMap['Contained Conversations'].color,
                        percentage: getPercentage(containedCallsValue, containableCallsValue)
                    }
                ];
            } else if (isContainableCallsView) {
                // Containable Conversations view (only Containable + Contained, no All Conversations)
                // Contained is % of Containable
                return [
                    {
                        name: 'Containable Conversations',
                        value: containableCallsValue,
                        color: seriesMap['Containable Conversations'].color
                    },
                    {
                        name: 'Contained Conversations',
                        value: containedCallsValue,
                        color: seriesMap['Contained Conversations'].color,
                        percentage: getPercentage(containedCallsValue, containableCallsValue)
                    }
                ];
            } else if (isSpanishCallsView) {
                // Spanish Calls view: similar nested structure
                return [
                    {
                        name: 'All Spanish Calls',
                        value: allSpanishCallsValue,
                        color: seriesMap['All Spanish Calls'].color
                    },
                    {
                        name: 'Spanish Containable Conversations',
                        value: spanishContainableValue,
                        color: seriesMap['Spanish Containable Conversations'].color,
                        percentage: getPercentage(spanishContainableValue, allSpanishCallsValue)
                    },
                    {
                        name: 'Spanish Contained Conversations',
                        value: spanishContainedValue,
                        color: seriesMap['Spanish Contained Conversations'].color,
                        percentage: getPercentage(spanishContainedValue, spanishContainableValue)
                    }
                ];
            } else {
                // Other views (Average Calls, Concurrent Calls, etc.): just show raw values
                // Filter out 'undefined' or 'Unknown' series names
                return Object.entries(seriesMap)
                    .filter(([name]) => name !== 'undefined' && name !== 'Unknown')
                    .map(([name, data]) => ({
                        name,
                        value: data.value,
                        color: data.color
                    }));
            }
        };

        const displayData = getDisplayData();

        const rawDate = slice.points[0]?.data?.fullDate || slice.points[0]?.data?.x;

        // Determine if this is the first data point (near left edge)
        // slice.x is the pixel position of the slice within the chart
        const isFirstPoint = slice.x !== undefined && slice.x < 60;

        // Compact mode for mobile - render inline simplified tooltip
        if (isMobile) {
            const formattedDateMobile = formatDateCompact(rawDate);
            // Show only the first/primary series for compact view
            const primaryData = displayData[0];
            return (
                <div
                    style={{
                        background: tooltipStyles.container.background,
                        backdropFilter: tooltipStyles.container.backdropFilter,
                        padding: '10px 14px',
                        borderRadius: tooltipStyles.container.borderRadius,
                        boxShadow: tooltipStyles.container.boxShadow,
                        border: tooltipStyles.container.border,
                        maxWidth: '180px',
                        textAlign: 'center' as const,
                        // Shift tooltip to the right if it's the first point to avoid going off-screen
                        transform: isFirstPoint ? 'translateX(100%)' : undefined
                    }}
                >
                    <div style={{
                        fontSize: '11px',
                        color: color_shades_darker,
                        marginBottom: 6
                    }}>
                        {formattedDateMobile}
                    </div>
                    {primaryData && (
                        <div style={{ marginBottom: displayData.length > 1 ? 4 : 0 }}>
                            <span style={{ fontWeight: 600, fontSize: '14px', color: color_shades_darkest }}>
                                {primaryData.value.toLocaleString()}
                            </span>
                            <span style={{ fontSize: '11px', color: color_shades_darker, marginLeft: 4 }}>
                                {primaryData.name.replace(' Calls', '').toLowerCase()}
                            </span>
                        </div>
                    )}
                    {displayData.length > 1 && displayData[displayData.length - 1].percentage && (
                        <div style={{
                            fontSize: '10px',
                            color: color_shades_darker,
                            lineHeight: 1.3
                        }}>
                            {displayData[displayData.length - 1].percentage} contained
                        </div>
                    )}
                </div>
            );
        }

        const formattedDate = formatDate(rawDate);

        // Position tooltip - normally to the left, but to the right if near the left edge
        const tooltipWidth = 280;
        const padding = 15;

        // Check if tooltip would go off the left edge, if so position to the right
        const wouldGoOffLeft = position.x - tooltipWidth - padding < 10;
        const leftPosition = wouldGoOffLeft
            ? position.x + padding
            : position.x - tooltipWidth - padding;

        const tooltipContent = (
            <div
                className={tooltipStyle}
                style={{
                    left: leftPosition,
                    top: position.y - 10
                }}
            >
                <p className="date-label">{formattedDate}</p>
                {displayData.map((series, index) => (
                    <div key={series.name} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px',
                        marginTop: index === 0 ? 0 : 6,
                        paddingTop: index === 0 ? 0 : 6,
                        borderTop: index === 0 ? 'none' : `1px solid ${color_shades_light}`
                    }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: ttBody, color: color_shades_darker }}>
                            <span style={{
                                ...tooltipStyles.colorIndicator,
                                backgroundColor: series.color,
                                border: `2px solid ${series.color}`,
                                opacity: 0.9
                            }} />
                            {series.name}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontWeight: 600, fontSize: ttValue, color: color_shades_darkest }}>
                                {series.value.toLocaleString()}
                            </span>
                            {series.percentage && (
                                <span style={{ fontWeight: 500, fontSize: ttBody, color: color_shades_darker }}>
                                    ({series.percentage})
                                </span>
                            )}
                        </span>
                    </div>
                ))}
            </div>
        );

        return ReactDOM.createPortal(tooltipContent, document.body);
    };

    const SliceTooltip = ({ slice }: any) => {
        return <PortalTooltip slice={slice} />;
    };

    // Calculate tick values to display based on data length and device size
    const getTickValues = (): number | string[] | undefined => {
        if (!props.data || props.data.length === 0) return undefined;

        const dataLength = props.data[0]?.data?.length || 0;

        if (isMobile) {
            // On mobile, show fewer ticks based on data density
            if (dataLength <= 7) {
                // 7 days or less: show all
                return undefined;
            } else if (dataLength <= 14) {
                // 8-14 points: show every other
                return props.data[0]?.data
                    ?.filter((_: any, i: number) => i % 2 === 0)
                    .map((d: any) => d.x) || undefined;
            } else if (dataLength <= 24) {
                // 15-24 points (hours): show every 4th (6 labels)
                return props.data[0]?.data
                    ?.filter((_: any, i: number) => i % 4 === 0)
                    .map((d: any) => d.x) || undefined;
            } else {
                // More than 24 points: show every 5th
                return props.data[0]?.data
                    ?.filter((_: any, i: number) => i % 5 === 0)
                    .map((d: any) => d.x) || undefined;
            }
        }

        // Desktop: show more labels but still limit if very dense
        if (dataLength > 31) {
            return props.data[0]?.data
                ?.filter((_: any, i: number) => i % 3 === 0)
                .map((d: any) => d.x) || undefined;
        }

        return undefined; // nivo will auto-determine
    };

    return (
        <div className={chartContainerStyle}>
            <ResponsiveLine
                data={props.data}
                enableArea={true}
                areaOpacity={0.08}
                animate={true}
                curve="monotoneX"
                enableGridX={false}
                colors={(series) => series.color}
                lineWidth={isMobile ? 2 : 2.5}
                pointSize={isMobile ? 3 : 5}
                pointBorderColor={color_defaults_white}
                pointBorderWidth={isMobile ? 1 : 1.5}
                pointColor={(point) => point.series.color}
                useMesh
                enableSlices="x"
                sliceTooltip={SliceTooltip}
                margin={{
                    top: isMobile ? 8 : 10,
                    right: isMobile ? 35 : 55,
                    bottom: isMobile ? 50 : 45,
                    left: isMobile ? 30 : 55
                }}
                axisTop={null}
                axisLeft={null}
                axisBottom={{
                    tickSize: 0,
                    tickPadding: isMobile ? 3 : 5,
                    tickRotation: isMobile ? -55 : -45,
                    tickValues: getTickValues()
                }}
                axisRight={{
                    tickSize: 0,
                    tickPadding: isMobile ? 4 : 8,
                    tickRotation: 0,
                    tickValues: isMobile ? 5 : undefined
                }}
                theme={{
                    background: '#ffffff',
                    tooltip: {
                        container: {
                            fontSize: "13px"
                        }
                    },
                    labels: {
                        text: { color: "#555" }
                    }
                }}
                legends={[]}
            />
        </div>
    );
};

interface HistogramChartProps {
    title: string
    subtitle?: string
    primaryDataName?: string
    secondaryDataName?: string
    data: { x: string, y: number, color: string, fullDate: string, link?: string }[]
    secondaryData: { x: string, y: number, color: string, fullDate: string, link?: string }[]
    showSecondary: boolean
    showPrimary: boolean
    isLoading?: boolean
    filterButton?: ReactElement
    titlePadding?: { top: number, right: number, bottom: number, left: number }
    titleMargin?: { top: number, right: number, bottom: number, left: number }
    graphPadding?: number
    graphBottomPadding?: number
    titleStyle?: string
    titleStyles?: React.CSSProperties
    scale?: number
    // New props for dropdown functionality
    showViewDropdown?: boolean
    selectedView?: HistogramViewOption
    onViewChange?: (view: HistogramViewOption) => void
    disableViewDropdown?: boolean
    multiLineData?: Array<{ id: string, data: { x: string, y: number, color: string, fullDate: string, link?: string }[], color: string }>
    // Export button prop
    onExportClick?: () => void
    exportButtonText?: string
    // Legend tooltips - maps line id to metric definition key
    legendTooltips?: Record<string, string>
    // Custom legend tooltip component renderer
    renderLegendTooltip?: (lineId: string, metricKey: string) => ReactElement
    // Legend counts - maps line id to count number (displayed in parentheses before tooltip)
    legendCounts?: Record<string, number>
    // Custom labels for view options in dropdown (maps internal value to display label)
    viewOptionLabels?: Record<string, string>
    // Hide Spanish Calls option from dropdown (for non-multilingual organizations)
    hideSpanishCallsOption?: boolean
    // Hide Concurrent Calls option from dropdown (only show when single app is selected)
    hideConcurrentCallsOption?: boolean
    // Hide Containable Conversations option from dropdown (for dashboards using Order Calls view)
    hideContainableCallsOption?: boolean
    // Hide Order Calls option from dropdown (for non-ordering dashboards like dental scheduling)
    hideOrderCallsOption?: boolean
}
export const HistogramChartCard: React.SFC<HistogramChartProps> = (props) => {

    const allViewOptions: HistogramViewOption[] = ["All Conversations", "Containable Conversations", "Order Calls", "Average Calls by Day of Week", "Average Calls by Hour", "Concurrent Calls", "Spanish Calls"];
    const viewOptions = allViewOptions.filter(option => {
        if (props.hideSpanishCallsOption && option === "Spanish Calls") return false;
        if (props.hideConcurrentCallsOption && option === "Concurrent Calls") return false;
        if (props.hideContainableCallsOption && option === "Containable Conversations") return false;
        if (props.hideOrderCallsOption && option === "Order Calls") return false;
        return true;
    });

    const containerStyle = css`
    height: 100%;
    width: auto;
    font-family: Muli;
    border: 1px solid #CBCCD2;
    box-sizing: border-box;
    border-radius: 8px;
    flex: 1;
    background: white;
    overflow: visible;
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;

    @media (min-width: 1440px) and (max-width: 1679.98px) {
        padding-bottom: 14px;
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        padding-bottom: 12px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        padding-bottom: 10px;
    }

    @media (max-width: 640px) {
        padding-bottom: 12px;
    }
    .loader-container {
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        .spinner > div {
            background: ${color_colors_ocean};
        }
    }

    .title-section {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        flex-wrap: wrap;
        gap: 8px;
        margin: ${props.titleMargin ? `${props.titleMargin.top}px ${props.titleMargin.right}px ${props.titleMargin.bottom}px ${props.titleMargin.left}px` : (props.subtitle ? '0px 0px 24px 0px' : '0px')};
        padding: ${props.titlePadding ? `${props.titlePadding.top}px ${props.titlePadding.right}px ${props.titlePadding.bottom}px ${props.titlePadding.left}px` : (props.subtitle ? '16px' : '16px 16px 0px 16px')};
        
        /* Mobile: stack vertically */
        @media (max-width: 640px) {
            flex-direction: column;
            align-items: flex-start;
            padding: 12px 12px 8px 12px;
            gap: 8px;
        }
        
        .title-with-dropdown {
            display: flex;
            align-items: center;
            gap: 12px;
            position: relative;
            z-index: 10;
            
            @media (max-width: 640px) {
                gap: 8px;
                width: 100%;
                flex-wrap: wrap;
            }
        }

        .export-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 4px 10px;
            background: #2C3D52;
            color: white;
            border: none;
            border-radius: 16px;
            font-size: 10px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
            position: relative;
            z-index: 10;
            letter-spacing: 0.1px;

            /* Mac 13" at More Space scaling */
            @media (min-width: 1440px) and (max-width: 1679.98px) {
                padding: 4px 10px;
                font-size: 11px;
            }

            /* Mac 13" at default/tight scaling */
            @media (min-width: 1280px) and (max-width: 1439.98px) {
                padding: 4px 9px;
                font-size: 10px;
            }

            /* Chromebook / smaller laptops */
            @media (min-width: 1024px) and (max-width: 1279.98px) {
                padding: 3px 7px;
                font-size: 9px;
            }
            
            @media (max-width: 640px) {
                padding: 5px 12px;
                font-size: 12px;
            }

            &:hover:not(:disabled) {
                background: #3a5068;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }

            &:active:not(:disabled) {
                transform: scale(0.98);
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .title-label {
            font-size: 18px;
            line-height: 24px;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: ${color_text_light};
            margin: 0;
            text-align: center;
            
            @media (max-width: 640px) {
                font-size: 14px;
                line-height: 20px;
            }
        }

        .view-dropdown {
            position: relative;
            z-index: 10;
            select {
                font-size: 15px;
                font-weight: 600;
                color: #2D2D39;
                padding: 4px 24px 4px 8px;
                border: 1px solid ${color_shades_light};
                border-radius: 4px;
                background: white;
                cursor: pointer;
                appearance: none;
                font-family: Muli;

                @media (min-width: 1280px) and (max-width: 1439.98px) {
                    font-size: 10px;
                    padding: 3px 20px 3px 6px;
                }

                @media (min-width: 1024px) and (max-width: 1279.98px) {
            font-size: 8px;
                    padding: 3px 18px 3px 5px;
                }
                
                @media (max-width: 640px) {
                    font-size: 13px;
                    padding: 4px 20px 4px 6px;
                }
                
                &:hover:not(:disabled) {
                    border-color: ${color_colors_ocean};
                }

                &:focus {
                    outline: none;
                    border-color: ${color_colors_ocean};
                }

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    background: #f5f5f5;
                    color: #888;
                }
            }

            &::after {
                content: '▼';
                position: absolute;
                right: 8px;
                top: 50%;
                transform: translateY(-50%);
                pointer-events: none;
                font-size: 10px;
                color: #2D2D39;
                
                @media (max-width: 640px) {
                    right: 6px;
                    font-size: 8px;
                }
            }
        }

        .legend-container {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            margin-right: 0;
            margin-left: auto;
            gap: 4px 0;
            
            @media (max-width: 640px) {
                margin-left: 0;
                width: 100%;
                justify-content: flex-start;
                gap: 4px 8px;
            }

            >img {
                margin-right: 8px;
                
                @media (max-width: 640px) {
                    margin-right: 4px;
                    width: 10px;
                    height: 10px;
                }
            }
            
            > div {
                @media (max-width: 640px) {
                    width: 10px !important;
                    height: 10px !important;
                    margin-right: 3px !important;
                }
            }
            
            >p {                
                font-style: normal;
                font-weight: normal;
                font-size: 14px;
                line-height: 20px;
                text-align: right;
                margin-right: 16px;

                @media (min-width: 1280px) and (max-width: 1439.98px) {
                    font-size: 10px !important;
                    line-height: 15px;
                    margin-right: 10px !important;
                }

                /* Chromebook / smaller laptops */
                @media (min-width: 1024px) and (max-width: 1279.98px) {
                    font-size: 9px !important;
                    line-height: 14px;
                    margin-right: 8px !important;
                }
                
                @media (max-width: 640px) {
                    font-size: 10px !important;
                    line-height: 14px;
                    margin-right: 6px !important;
                    text-align: left;
                }
            }
        }
    }
    
    .subtitle-section {
        display: flex;
        align-items: center;
        margin-top: -7px;
        padding: 0px 36px;
        margin-left: -7px;
        
        @media (max-width: 640px) {
            padding: 0px 12px;
            margin-left: 0;
            margin-top: -4px;
        }
        
        h4 {
            font-style: normal;
            font-weight: normal;
            font-color: ${color_text_light};
            font-size: 12px;
            line-height: 18px;
            flex: 1;
            
            @media (max-width: 640px) {
                font-size: 10px;
                line-height: 14px;
            }
        }
    }
`

    // Prepare data for the chart
    const chartData = props.multiLineData || (props.showPrimary || props.showSecondary ? [
        ...(props.showPrimary ? [{ id: props.primaryDataName, data: props.data, color: ocean_blue }] : []),
        ...(props.showSecondary ? [{ id: props.secondaryDataName, data: props.secondaryData, color: ocean_blue }] : [])
    ] : []);

    return (
        <div className={containerStyle}>
            <div className="title-section ac-analytics-histogram-card">
                {props.showViewDropdown ? (
                    <div className="title-with-dropdown">
                        <div className="view-dropdown">
                            <select
                                value={props.selectedView || "Containable Conversations"}
                                onChange={(e) => props.onViewChange?.(e.target.value as HistogramViewOption)}
                                disabled={props.disableViewDropdown}
                            >
                                {viewOptions.map(option => (
                                    <option key={option} value={option}>{props.viewOptionLabels?.[option] || option}</option>
                                ))}
                            </select>
                        </div>
                        {props.onExportClick && (props.selectedView === "All Conversations" || props.selectedView === "Containable Conversations") && (
                            <button className="export-button" onClick={props.onExportClick} data-view-export-button disabled={props.isLoading}>
                                {props.exportButtonText || 'View Details'}
                            </button>
                        )}
                    </div>
                ) : (
                    <h5 className={props.titleStyle || "title-label"} style={props.titleStyles}>{props.title}</h5>
                )}
                <div className="legend-container">
                    {props?.filterButton === undefined ? (<>
                        {props.multiLineData ? (
                            // Show legend for multi-line data
                            props.multiLineData.map((line, idx) => (
                                <React.Fragment key={line.id}>
                                    <div style={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: line.color,
                                        borderRadius: 2,
                                        marginRight: 4
                                    }} />
                                    <p style={{
                                        marginRight: idx < props.multiLineData.length - 1 ? 16 : 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '2px'
                                    }}>
                                        {line.id}
                                        {props.legendCounts?.[line.id] !== undefined && (
                                            <span style={{ marginLeft: 2 }}>({props.legendCounts[line.id].toLocaleString()})</span>
                                        )}
                                        {props.legendTooltips?.[line.id] && props.renderLegendTooltip &&
                                            props.renderLegendTooltip(line.id, props.legendTooltips[line.id])}
                                    </p>
                                </React.Fragment>
                            ))
                        ) : (<>
                            {props.showSecondary && <>
                                <img src={secondaryIndicator} />
                                <p>{props.secondaryDataName}</p>
                            </>}
                            {props.showPrimary && <>
                                <img src={primaryIndicator} />
                                <p>{props.primaryDataName}</p>
                            </>}
                        </>)}
                    </>) : props.filterButton}
                </div>
            </div>
            {props.subtitle && (
                <div className="subtitle-section ac-analytics-histogram-card">
                    <h4>{props.subtitle}</h4>
                </div>
            )}
            {props.isLoading
                ? <div className="loader-container"><SecondaryLoader /></div>
                : <HistogramChartMultiLine
                    data={chartData}
                    padding={props.graphPadding}
                    paddingBottom={props.graphBottomPadding}
                    hasSubtitle={!!props.subtitle}
                    scale={props.scale}
                />}
        </div>
    )
}

export default HistogramChartCard;